import {
  PlanStatus,
  PlanType,
  PlatformType,
  UserPermission,
  type BillingResponse,
  type Plan,
  type PlanItem,
  type PlanResponse,
} from '@/shared/graphql/legacy/generated'
import { useUserStore } from '@/shared/stores/user'
import { useWebviewStore } from '@/shared/stores/webview'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface SetPlanArgs {
  plan: Plan
  planItem: PlanItem
  billingCycle: {
    type: string
    description: string
  }
}

export const useBillingStore = defineStore('billing', () => {
  const userStore = useUserStore()
  const webviewStore = useWebviewStore()

  // Billing data
  const billing = ref<BillingResponse>()

  const setBilling = (data: BillingResponse) => {
    billing.value = data
  }

  const isSubscribedOnCurrentPlatform = computed(() => {
    if (webviewStore.isiOS) {
      return billing.value?.plan.platform === PlatformType.Apple
    }

    if (webviewStore.isAndroid) {
      return billing.value?.plan.platform === PlatformType.Google
    }

    return billing.value?.plan.platform === PlatformType.Web
  })

  const isPro = computed(() => billing.value?.plan.type === PlanType.Pro)

  const canUpdateSubscription = computed(
    () =>
      !userStore.user?.permissions.includes(UserPermission.Pro) ||
      isSubscribedOnCurrentPlatform.value ||
      (!isSubscribedOnCurrentPlatform.value &&
        billing.value?.plan.status === PlanStatus.Canceled)
  )

  // Plan selection state
  const selectedPlan = ref<Plan>()
  const selectedPlanItem = ref<PlanItem>()
  const selectedBillingCycle = ref({
    type: '',
    description: '',
  })

  const setPlanData = ({ plan, planItem, billingCycle }: SetPlanArgs) => {
    selectedPlan.value = plan
    selectedPlanItem.value = planItem
    selectedBillingCycle.value = billingCycle
  }

  const webviewPlans = ref<PlanResponse>()

  const setWebviewPlans = (data: PlanResponse) => {
    webviewPlans.value = data
  }

  return {
    billing,
    setBilling,
    isPro,
    isSubscribedOnCurrentPlatform,
    canUpdateSubscription,
    selectedPlan,
    selectedPlanItem,
    selectedBillingCycle,
    setPlanData,
    webviewPlans,
    setWebviewPlans,
  }
})
